import type { BudgetPhaseForm } from "../components/GuidedBudgetEditor/BudgetSelector";
import { SCHEDULED_BUDGET_FIRST_DELETABLE_PHASE_INDEX } from "../components/GuidedBudgetEditor/guidedBudgetEditorMoney";
import { clampScheduledPhaseWallPatch } from "./eventWallTime/scheduledPhaseWallClamp";
import type { ScheduledBudgetAdDeliveryWindowUtc } from "./eventWallTime/scheduledPhaseWallClamp";
import { initialTwoScheduledPhaseWallRows } from "./eventWallTime";

function cloneSegments(segments: BudgetPhaseForm[]): BudgetPhaseForm[] {
  return segments.map((s) => ({
    ...s,
    amount: { ...s.amount },
  }));
}

/** When campaign / ad flight end is known, keep the final phased row’s `time_end` aligned to it. */
function pinLastScheduledSegmentEndToCampaignWall(
  rows: BudgetPhaseForm[],
  lastPhaseEndWall?: string | null
): BudgetPhaseForm[] {
  const wall = lastPhaseEndWall?.trim();
  if (!wall || rows.length === 0) return rows;
  const out = cloneSegments(rows);
  const li = out.length - 1;
  out[li] = { ...out[li], time_end: wall };
  return out;
}

/** Enforces a strict contiguous chain: each phase start equals the previous phase end. */
function normalizeSegmentsContiguous(rows: BudgetPhaseForm[]): BudgetPhaseForm[] {
  for (let i = 1; i < rows.length; i++) {
    rows[i] = {
      ...rows[i],
      time_start: rows[i - 1]?.time_end ?? "",
    };
  }
  return rows;
}

/**
 * Applies a patch to one budget phase and keeps the timeline contiguous:
 * each phase `time_end` equals the next phase `time_start`, and editing a
 * later phase start moves the previous phase end to match.
 */
export function applyBudgetSegmentPatchContiguous(
  segments: BudgetPhaseForm[],
  index: number,
  patch: Partial<BudgetPhaseForm>
): BudgetPhaseForm[] {
  const rows = cloneSegments(segments);
  const row = rows[index];
  if (!row) return rows;

  const merged: BudgetPhaseForm = {
    ...row,
    ...patch,
    amount:
      patch.amount != null
        ? { ...row.amount, ...patch.amount }
        : { ...row.amount },
  };
  rows[index] = merged;

  if (index + 1 < rows.length) {
    rows[index + 1] = {
      ...rows[index + 1],
      time_start: merged.time_end,
    };
  }
  if (index > 0) {
    rows[index - 1] = {
      ...rows[index - 1],
      time_end: merged.time_start,
    };
  }
  return normalizeSegmentsContiguous(rows);
}

/** After removing a middle phase, snap the following phase start to the previous end. */
export function removeBudgetPhaseAndReconnect(
  segments: BudgetPhaseForm[],
  removedIndex: number,
  options?: { lastPhaseEndWall?: string | null }
): BudgetPhaseForm[] {
  const len = segments.length;
  if (len <= 2) {
    return cloneSegments(segments);
  }
  // Phases at indexes 0–1 are mandatory; only index 2+ may be removed.
  if (removedIndex < SCHEDULED_BUDGET_FIRST_DELETABLE_PHASE_INDEX) {
    return cloneSegments(segments);
  }
  const rows = cloneSegments(segments);
  const removed = rows[removedIndex];
  const next = rows.filter((_, j) => j !== removedIndex);
  const removedEnd = removed?.time_end?.trim() ?? "";
  if (
    removedIndex === len - 1 &&
    next.length > 0 &&
    next[next.length - 1] != null &&
    removedEnd
  ) {
    next[next.length - 1] = {
      ...next[next.length - 1],
      time_end: removedEnd,
    };
  }
  if (
    removedIndex > 0 &&
    removedIndex < len - 1 &&
    next[removedIndex] != null &&
    next[removedIndex - 1] != null
  ) {
    next[removedIndex] = {
      ...next[removedIndex],
      time_start: next[removedIndex - 1].time_end,
    };
  }
  return pinLastScheduledSegmentEndToCampaignWall(
    normalizeSegmentsContiguous(next),
    options?.lastPhaseEndWall
  );
}

/** Appends a phase; if the new row has no start, it inherits the previous phase end. */
/**
 * Event-wall `time_end` of the last budget phase when set, for keeping campaign `schedule.end_at`
 * aligned in the guided create flow (`scheduled` budget).
 */
export function lastScheduledPhaseEndWall(
  segments: BudgetPhaseForm[] | undefined
): string | null {
  const segs = segments ?? [];
  if (segs.length === 0) return null;
  const wall = segs[segs.length - 1]?.time_end?.trim();
  return wall && wall.length > 0 ? wall : null;
}

export type ReconcileScheduledBudgetSegmentsParams = {
  segments: BudgetPhaseForm[];
  /** Event-wall `YYYY-MM-DDTHH:mm` for campaign start (e.g. {@link wireScheduleToEventWall}). */
  scheduleStartWall: string;
  /** Event-wall campaign end (same convention as start). */
  scheduleEndWall: string;
  eventTimezone: string;
  /** Full ad delivery window (UTC ISO) from the **new** run schedule; clamps each phase into the window. */
  deliveryWindow: ScheduledBudgetAdDeliveryWindowUtc | null;
};

/**
 * When the user changes campaign run dates, phased segment walls can still hold the previous
 * window (especially the last phase `time_end`). Re-pin the chain to the new schedule and clamp
 * each phase into {@link deliveryWindow} so create-flow / Yup validation matches the UI again.
 */
export function reconcileScheduledBudgetSegmentsToCampaignSchedule(
  params: ReconcileScheduledBudgetSegmentsParams
): BudgetPhaseForm[] {
  const { segments, scheduleStartWall, scheduleEndWall, eventTimezone, deliveryWindow } =
    params;
  if (!Array.isArray(segments) || segments.length < 2) {
    return cloneSegments(segments ?? []);
  }

  const startWall = scheduleStartWall?.trim() ?? "";
  const endWall = scheduleEndWall?.trim() ?? "";
  let rows = cloneSegments(segments);

  if (startWall) {
    rows[0] = { ...rows[0], time_start: startWall };
  }
  if (endWall) {
    const li = rows.length - 1;
    rows[li] = { ...rows[li], time_end: endWall };
  }
  rows = normalizeSegmentsContiguous(rows);

  for (let iter = 0; iter < 20; iter++) {
    const before = JSON.stringify(rows);
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const patch = clampScheduledPhaseWallPatch(
        {
          time_start: row.time_start?.trim() ?? "",
          time_end: row.time_end?.trim() ?? "",
        },
        {},
        eventTimezone,
        deliveryWindow
      );
      rows[i] = {
        ...row,
        ...patch,
        amount: { ...row.amount },
      };
    }
    rows = normalizeSegmentsContiguous(rows);
    if (JSON.stringify(rows) === before) break;
  }

  if (startWall) {
    rows[0] = { ...rows[0], time_start: startWall };
  }
  if (endWall) {
    const li = rows.length - 1;
    rows[li] = { ...rows[li], time_end: endWall };
  }
  rows = normalizeSegmentsContiguous(rows);

  return rows;
}

export function appendBudgetPhaseContiguous(
  segments: BudgetPhaseForm[],
  phase: BudgetPhaseForm,
  options?: {
    /**
     * When set, appending can prefill by splitting the previous last phase into two contiguous
     * windows in this event zone (keeps the campaign tail looking complete by default).
     */
    prefillBySplittingLastPhaseInEventTimezone?: string;
    /**
     * Event-wall `YYYY-MM-DDTHH:mm` for campaign / ad flight end — last segment `time_end` is set
     * to this after add (and should be passed on remove) so the chain always closes on schedule.
     */
    lastPhaseEndWall?: string | null;
  }
): BudgetPhaseForm[] {
  const rows = cloneSegments(segments);
  const last = rows[rows.length - 1];
  const splitTimezone = options?.prefillBySplittingLastPhaseInEventTimezone?.trim();
  const hasLastWalls = Boolean(last?.time_start?.trim() && last?.time_end?.trim());
  if (splitTimezone && last && hasLastWalls) {
    const [splitA, splitB] = initialTwoScheduledPhaseWallRows({
      eventTimezone: splitTimezone,
      scheduleStartWall: last.time_start,
      scheduleEndWall: last.time_end,
    });
    if (
      splitA.time_start?.trim() &&
      splitA.time_end?.trim() &&
      splitB.time_start?.trim() &&
      splitB.time_end?.trim()
    ) {
      rows[rows.length - 1] = {
        ...last,
        time_start: splitA.time_start,
        time_end: splitA.time_end,
      };
      return pinLastScheduledSegmentEndToCampaignWall(
        normalizeSegmentsContiguous([
          ...rows,
          {
            ...phase,
            time_start: splitB.time_start,
            time_end: splitB.time_end,
            amount: { ...phase.amount },
          },
        ]),
        options?.lastPhaseEndWall
      );
    }
  }
  const rawStart = phase.time_start?.trim();
  const time_start =
    rawStart && rawStart.length > 0
      ? phase.time_start
      : last?.time_end?.trim() ?? "";
  return pinLastScheduledSegmentEndToCampaignWall(
    normalizeSegmentsContiguous([
      ...rows,
      {
        ...phase,
        time_start,
        amount: { ...phase.amount },
      },
    ]),
    options?.lastPhaseEndWall
  );
}
